/**
 * openCheckout — keeps external checkout URLs (Viva Wallet, etc.) INSIDE the
 * native Allure app on Android/iOS instead of kicking the user out to the
 * system browser.
 *
 * On native Capacitor (`Capacitor.isNativePlatform()`), we open the URL in
 * Chrome Custom Tabs (Android) or SFSafariViewController (iOS) via
 * `@capacitor/browser`. Both surfaces stay skinned in Allure's brand color,
 * show the app's title bar, and pop back to the app when the user is done.
 *
 * On web / PWA, we fall back to a same-tab redirect (`window.location.href`)
 * so the Safari PWA and desktop web experiences are unchanged.
 *
 * Usage:
 *   const { unsubscribe } = onCheckoutClosed(async () => {
 *     await reconcileCard();   // pull the latest state once the user returns
 *   });
 *   await openCheckout(url);   // presents the in-app browser
 *   // ... unsubscribe() is called automatically inside the handler
 */
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const ACCENT_HEX = '#703FF6';

export function isNativeCheckoutSupported() {
  try {
    return Capacitor.isNativePlatform();
  } catch (_e) {
    return false;
  }
}

/**
 * Present the URL in an in-app browser surface on native, otherwise
 * redirect the current WebView to the URL.
 *
 * Returns { mode: 'in-app' | 'redirect' }.
 */
export async function openCheckout(url) {
  if (!url) throw new Error('openCheckout: url is required');

  if (isNativeCheckoutSupported()) {
    try {
      await Browser.open({
        url,
        // Android → Chrome Custom Tabs toolbar color.
        toolbarColor: ACCENT_HEX,
        // iOS → SFSafariViewController presentation.
        presentationStyle: 'fullscreen',
        // Keep the browser as the same "window" so Back returns to the app.
        windowName: '_self',
      });
      return { mode: 'in-app' };
    } catch (err) {
      // Older APKs shipped before the Android build was wired up to the
      // @capacitor/browser plugin throw:
      //   `"Browser" plugin is not implemented on android`
      // Rather than dead-ending the user, fall through to a same-WebView
      // redirect so they can still complete the flow. Newer APKs (with the
      // plugin properly registered in capacitor.plugins.json /
      // capacitor.settings.gradle / capacitor.build.gradle) never hit this
      // branch and stay inside the Custom Tab overlay.
      const msg = String(err?.message || '').toLowerCase();
      const notImplemented = msg.includes('not implemented') || msg.includes('not available');
      if (!notImplemented) throw err;
      window.location.href = url;
      return { mode: 'redirect' };
    }
  }

  window.location.href = url;
  return { mode: 'redirect' };
}

/**
 * Register a handler that fires when the user dismisses the in-app browser
 * (Custom Tab / SFSafariViewController). Returns an `unsubscribe()` fn.
 *
 * On non-native environments this is a no-op — the browser doesn't stay
 * open, so there's nothing to observe.
 */
export function onCheckoutClosed(handler) {
  if (!isNativeCheckoutSupported() || typeof handler !== 'function') {
    return () => {};
  }

  let listenerHandle = null;
  let disposed = false;

  const pending = Browser.addListener('browserFinished', () => {
    try {
      handler();
    } catch (_e) {
      // Swallow — handler errors shouldn't blow up the caller.
    }
  });

  pending
    .then((handle) => {
      if (disposed) {
        try { handle.remove(); } catch (_e) { /* ignore */ }
      } else {
        listenerHandle = handle;
      }
    })
    .catch(() => { /* ignore — listener never attached */ });

  return () => {
    disposed = true;
    if (listenerHandle) {
      try { listenerHandle.remove(); } catch (_e) { /* ignore */ }
      listenerHandle = null;
    }
  };
}

/**
 * Programmatically close the in-app browser (Android/iOS only). Safe to
 * call from any environment — resolves to `false` when not supported.
 */
export async function closeCheckout() {
  if (!isNativeCheckoutSupported()) return false;
  try {
    await Browser.close();
    return true;
  } catch (_e) {
    return false;
  }
}
